/**
 * The presenter session wired to the real app: the shared stores, the globe
 * engine and twin handle accessors, the Guam demo, the procedure loader, the
 * demo-data loaders, toasts and browser timers. Tests never import this file
 * (it pulls in Cesium through LoaderGlobe); they build SessionDeps from fakes.
 */
import { useClock, useUI } from '@/state/store';
import { toast } from '@/components/ui/toast';
import { GUAM } from '@/features/loader/constants';
import { loadNetworkDataset } from '@/features/loader/data/loadNetwork';
import { loadStaticDataset } from '@/features/loader/data/staticData';
import { getGlobeEngine } from '@/features/loader/globe/engineHandle';
import { useLoader } from '@/features/loader/store';
import { addGuamStation, guamStationAdded, startGuamDemo, stopGuamDemo } from '@/features/loader/ui/guamDemo';
import { useOverview } from '@/features/overview/store';
import { getTwinHandle } from '@/features/twin/scene/TwinHandle';
import { useTwin } from '@/features/twin/store';
import { useLiveFeed } from '@/features/twin/telemetry/liveFeedStore';
import { loadProcedure } from '@/features/twin/ui/procedures/loadProcedures';
import { createDemoData } from './demoData';
import { requestGaiaPrefill } from './gaiaPrefill';
import type { SessionDeps } from './session';

/** Resolves after ms, or as soon as the signal aborts; never rejects. */
export function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const done = () => {
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    };
    const timer = window.setTimeout(done, ms);
    signal?.addEventListener('abort', done, { once: true });
  });
}

/** Stops the Guam demo and takes the Guam station back out of the network if a previous run added it. */
export function resetGuamDemo(): void {
  stopGuamDemo();
  const loader = useLoader.getState();
  if (loader.dataset && guamStationAdded()) {
    loader.patchDataset({ antennas: loader.dataset.antennas.filter((a) => a.id !== GUAM.STATION_ID) });
  }
}

/**
 * navigate is react-router's; renderedSearch returns the search of the location the router has
 * rendered (usePresenterKeys keeps it), which lags window.location while a navigation renders.
 */
export function createAppDeps(navigate: (to: string) => void, renderedSearch: () => string): SessionDeps {
  return {
    navigate,
    pathname: () => window.location.pathname,
    search: () => window.location.search,
    renderedSearch,
    ui: () => useUI.getState(),
    clock: () => useClock.getState(),
    loader: () => useLoader.getState(),
    twin: () => useTwin.getState(),
    liveFeed: () => useLiveFeed.getState(),
    overview: () => useOverview.getState(),
    globe: () => getGlobeEngine(),
    twinHandle: () => getTwinHandle(),
    guam: {
      reset: resetGuamDemo,
      start: startGuamDemo,
      addStation: addGuamStation,
      stationAdded: guamStationAdded,
    },
    loadProcedure: (id, ctx) => loadProcedure(id, ctx),
    gaiaPrefill: requestGaiaPrefill,
    sleep: abortableSleep,
    now: () => Date.now(),
    reducedMotion: () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    setTimer: (fn, ms) => window.setTimeout(fn, ms),
    clearTimer: (handle) => window.clearTimeout(handle as number),
    notifyError: (text) => toast('error', text),
    demoData: createDemoData({
      loader: () => useLoader.getState(),
      subscribe: (listener) => useLoader.subscribe(listener),
      loadBundled: (network) => loadStaticDataset(network),
      loadNetwork: (network) =>
        loadNetworkDataset(network, {
          onNotice: (n) =>
            toast(n.level === 'error' ? 'error' : n.level === 'warn' ? 'warn' : 'info', n.text),
        }),
      report: (text) => toast('error', text),
      globe: () => getGlobeEngine(),
      defer: (fn, ms) => {
        window.setTimeout(fn, ms);
      },
    }),
  };
}
